"use client"

import * as React from "react"
import { BookmarkPlus, Check, ChevronDown, Pencil, Star, Trash2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

// ---- Types ----
export interface ViewState {
  columnVisibility?: Record<string, boolean>
  columnOrder?: string[]
  columnSizing?: Record<string, number>
  sorting?: { id: string; desc: boolean }[]
  columnFilters?: { id: string; value: unknown }[]
  globalFilter?: string
  filterVisibility?: Record<string, boolean>
  filterOrder?: string[]
}

export interface SavedView {
  id: string
  name: string
  view: ViewState
  isDefault?: boolean
  createdAt: string
  updatedAt: string
}

// ---- Props ----
export interface SavedViewsMenuProps {
  /** localStorage namespace — typically the UnifiedDataTable `localStoragePrefix`. */
  storageKey: string
  /** Snapshot of the current table state built from useTablePersistence. */
  currentView: ViewState
  /** Apply a persisted ViewState back onto the table. */
  onApplyView: (view: ViewState) => void
}

// ---- Storage helpers ----
function viewsStorageKey(prefix: string) {
  return `${prefix}:saved-views`
}
function defaultIdStorageKey(prefix: string) {
  return `${prefix}:saved-views:default`
}
function activeIdStorageKey(prefix: string) {
  return `${prefix}:saved-views:active`
}

function readViews(prefix: string): SavedView[] {
  if (typeof window === "undefined") return []
  try {
    const raw = localStorage.getItem(viewsStorageKey(prefix))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as SavedView[]) : []
  } catch {
    return []
  }
}

function writeViews(prefix: string, views: SavedView[]) {
  if (typeof window === "undefined") return
  localStorage.setItem(viewsStorageKey(prefix), JSON.stringify(views))
}

function readDefaultId(prefix: string): string | null {
  if (typeof window === "undefined") return null
  return localStorage.getItem(defaultIdStorageKey(prefix))
}

function writeDefaultId(prefix: string, id: string | null) {
  if (typeof window === "undefined") return
  if (id) localStorage.setItem(defaultIdStorageKey(prefix), id)
  else localStorage.removeItem(defaultIdStorageKey(prefix))
}

function readActiveId(prefix: string): string | null {
  if (typeof window === "undefined") return null
  return localStorage.getItem(activeIdStorageKey(prefix))
}

function writeActiveId(prefix: string, id: string | null) {
  if (typeof window === "undefined") return
  if (id) localStorage.setItem(activeIdStorageKey(prefix), id)
  else localStorage.removeItem(activeIdStorageKey(prefix))
}

type DialogMode = "save" | "rename" | null

export function SavedViewsMenu({
  storageKey,
  currentView,
  onApplyView,
}: SavedViewsMenuProps) {
  const [mounted, setMounted] = React.useState(false)
  const [views, setViews] = React.useState<SavedView[]>([])
  const [activeId, setActiveId] = React.useState<string | null>(null)
  const [dialogMode, setDialogMode] = React.useState<DialogMode>(null)
  const [nameInput, setNameInput] = React.useState("")

  // Load on mount; auto-apply default if no active view.
  React.useEffect(() => {
    const loaded = readViews(storageKey)
    const defaultId = readDefaultId(storageKey)
    const priorActive = readActiveId(storageKey)
    setViews(
      loaded.map((v) => ({ ...v, isDefault: defaultId === v.id })),
    )
    setMounted(true)

    if (!priorActive && defaultId) {
      const def = loaded.find((v) => v.id === defaultId)
      if (def) {
        writeActiveId(storageKey, def.id)
        setActiveId(def.id)
        onApplyView(def.view)
        return
      }
    }
    setActiveId(priorActive)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey])

  const activeView = React.useMemo(
    () => views.find((v) => v.id === activeId) ?? null,
    [views, activeId],
  )

  const persistViews = React.useCallback(
    (next: SavedView[]) => {
      setViews(next)
      writeViews(storageKey, next)
    },
    [storageKey],
  )

  const openSaveDialog = React.useCallback(() => {
    setNameInput("")
    setDialogMode("save")
  }, [])

  const openRenameDialog = React.useCallback(() => {
    if (!activeView) return
    setNameInput(activeView.name)
    setDialogMode("rename")
  }, [activeView])

  const handleDialogSubmit = React.useCallback(() => {
    const name = nameInput.trim()
    if (!name) return
    const now = new Date().toISOString()

    if (dialogMode === "save") {
      const newView: SavedView = {
        id:
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `v_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        name,
        view: currentView,
        createdAt: now,
        updatedAt: now,
      }
      persistViews([...views, newView])
      setActiveId(newView.id)
      writeActiveId(storageKey, newView.id)
    } else if (dialogMode === "rename" && activeView) {
      persistViews(
        views.map((v) =>
          v.id === activeView.id ? { ...v, name, updatedAt: now } : v,
        ),
      )
    }
    setDialogMode(null)
  }, [dialogMode, nameInput, currentView, views, activeView, persistViews, storageKey])

  const handleApply = React.useCallback(
    (view: SavedView) => {
      setActiveId(view.id)
      writeActiveId(storageKey, view.id)
      onApplyView(view.view)
    },
    [onApplyView, storageKey],
  )

  const handleDelete = React.useCallback(() => {
    if (!activeView) return
    const next = views.filter((v) => v.id !== activeView.id)
    persistViews(next)
    if (readDefaultId(storageKey) === activeView.id) {
      writeDefaultId(storageKey, null)
    }
    setActiveId(null)
    writeActiveId(storageKey, null)
  }, [activeView, views, persistViews, storageKey])

  const handleSetDefault = React.useCallback(() => {
    if (!activeView) return
    writeDefaultId(storageKey, activeView.id)
    setViews((prev) =>
      prev.map((v) => ({ ...v, isDefault: v.id === activeView.id })),
    )
  }, [activeView, storageKey])

  const hasActive = !!activeView
  const triggerLabel = hasActive ? `Views: ${activeView!.name}` : "Views"

  if (!mounted) {
    return (
      <Button variant="outline" size="sm" className="h-9 gap-1.5" disabled>
        <BookmarkPlus className="h-4 w-4" />
        <span className="text-xs">Views</span>
        <ChevronDown className="h-3 w-3 opacity-60" />
      </Button>
    )
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "h-9 gap-1.5 max-w-60",
              hasActive && "border-primary/50 text-primary",
            )}
            title={triggerLabel}
          >
            <BookmarkPlus className="h-4 w-4 shrink-0" />
            <span className="text-xs truncate">{triggerLabel}</span>
            <ChevronDown className="h-3 w-3 opacity-60 shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuItem onSelect={openSaveDialog} className="text-xs gap-2">
            <BookmarkPlus className="h-3.5 w-3.5" />
            Save current view as…
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={openRenameDialog}
            disabled={!hasActive}
            className="text-xs gap-2"
          >
            <Pencil className="h-3.5 w-3.5" />
            Rename current
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={handleDelete}
            disabled={!hasActive}
            variant="destructive"
            className="text-xs gap-2"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete current
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={handleSetDefault}
            disabled={!hasActive}
            className="text-xs gap-2"
          >
            <Star className="h-3.5 w-3.5" />
            Set as default
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {views.length === 0 ? (
            <div className="px-2 py-1.5 text-[11px] text-muted-foreground">
              No saved views yet.
            </div>
          ) : (
            views.map((v) => (
              <DropdownMenuItem
                key={v.id}
                onSelect={() => handleApply(v)}
                className="text-xs gap-2"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  {v.isDefault ? (
                    <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-500" />
                  ) : activeId === v.id ? (
                    <Check className="h-3.5 w-3.5 text-primary" />
                  ) : null}
                </span>
                <span className="truncate flex-1">{v.name}</span>
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={dialogMode !== null}
        onOpenChange={(open) => !open && setDialogMode(null)}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === "rename" ? "Rename view" : "Save current view as…"}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Input
              autoFocus
              placeholder="Name"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  handleDialogSubmit()
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDialogMode(null)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleDialogSubmit}
              disabled={!nameInput.trim()}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
